import config from 'config';
import { getAuth } from 'firebase/auth';
import { connectAgentStream } from './agentStreamProtocol';
import { normalizeAgentThread } from './agentMessageContract';

const apiBase = config.apiUrl;

export async function startAgentStream({ content, messages = [], threadId = null, clarifications = null }) {
  // Return a config object instead of URL since we need POST
  return { content, messages, threadId, clarifications };
}

export function openAgentStream(config, handlers = {}) {
  return connectAgentStream(`${apiBase}/agentic/agent/orchestrate/stream`, {
    ...handlers,
    method: 'POST',
    body: {
      protocol_version: 2,
      content: config.content,
      messages: config.messages || [],
      thread_id: config.threadId || undefined
    }
  });
}

export async function stopAgentStream(threadId) {
  if (!threadId) return;
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return;
  try {
    const token = await user.getIdToken();
    const url = `${apiBase}/agentic/agent/orchestrate/stop/${encodeURIComponent(threadId)}`;
    await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      credentials: 'include'
    });
  } catch (_) {
    // ignore network errors on stop
  }
}

export async function rateChatMessage(messageId, isHelpful, note = '') {
  if (!messageId) throw new Error('messageId required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const url = `${apiBase}/agentic/chat/messages/${encodeURIComponent(messageId)}/feedback`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ is_helpful: !!isHelpful, note: note || '' })
  });
  if (!res.ok) {
    let err = {};
    try {
      err = await res.json();
    } catch (_) {
      // ignore
    }
    const msg = err?.error || `Failed (${res.status})`;
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

// Fetch paginated list of current user's threads
export async function fetchChatThreads({ page = 1, page_size = 8 } = {}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = new URL(`${apiBase}/agentic/chat/threads`);
  url.searchParams.set('page', String(page));
  url.searchParams.set('page_size', String(page_size));

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    },
    credentials: 'include'
  });
  if (!res.ok) throw new Error(`Failed to fetch threads (${res.status})`);
  const data = await res.json();

  // Normalize response fields
  const items = data.items || data.threads || data.results || [];
  const normalized = {
    page: Number(data.page || data.current || data.page_number || page || 1),
    page_size: Number(data.page_size || data.pageSize || page_size || (Array.isArray(items) ? items.length : 0) || 10),
    num_pages: Number(
      data.num_pages ||
        data.total_pages ||
        (data.total || data.count
          ? Math.max(1, Math.ceil((data.total || data.count) / (data.page_size || data.pageSize || page_size || 10)))
          : 1)
    ),
    total: Number(data.total || data.count || (Array.isArray(items) ? items.length : 0))
  };

  return { items, ...normalized, raw: data };
}

// Fetch a full thread including ordered messages and artifacts
export async function fetchChatThread(threadId) {
  if (!threadId) throw new Error('threadId required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = `${apiBase}/agentic/chat/threads/${encodeURIComponent(threadId)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    },
    credentials: 'include'
  });
  if (!res.ok) throw new Error(`Failed to fetch thread (${res.status})`);
  const data = await res.json();
  return normalizeAgentThread(data);
}

// Search current user's chat threads by title or last message preview
export async function searchChatThreads({ q, page = 1, page_size = 8 } = {}) {
  const term = (q || '').trim();
  if (!term) throw new Error('q is required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  // Agentic namespace (authoritative)
  const baseUrl = `${apiBase}/agentic/chat/threads/search`;
  const url = new URL(baseUrl);
  url.searchParams.set('q', term);
  url.searchParams.set('page', String(page));
  url.searchParams.set('page_size', String(page_size));

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    },
    credentials: 'include'
  });

  if (!res.ok) throw new Error(`Failed to search threads (${res.status})`);
  const data = await res.json();
  const items = data.items || data.threads || data.results || [];
  const normalized = {
    page: Number(data.page || page || 1),
    page_size: Number(data.page_size || page_size || (Array.isArray(items) ? items.length : 0) || 10),
    num_pages: Number(
      data.num_pages ||
        data.total_pages ||
        (data.total || data.count
          ? Math.max(1, Math.ceil((data.total || data.count) / (data.page_size || data.pageSize || page_size || 10)))
          : 1)
    ),
    total: Number(data.total || data.count || (Array.isArray(items) ? items.length : 0))
  };
  return { items, ...normalized, raw: data };
}

// Share or unshare a chat thread
export async function shareChatThread(threadId, isPublic) {
  if (!threadId) throw new Error('threadId required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = `${apiBase}/agentic/chat/threads/${encodeURIComponent(threadId)}/share`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    credentials: 'include',
    body: JSON.stringify({ is_public: !!isPublic })
  });

  if (!res.ok) {
    let err = {};
    try {
      err = await res.json();
    } catch (_) {
      // ignore
    }
    const msg = err?.error || `Failed to ${isPublic ? 'share' : 'unshare'} thread (${res.status})`;
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }

  return res.json();
}

// Fetch a public chat thread (no authentication required)
export async function fetchPublicChatThread(threadId, { limit = 100, offset = 0 } = {}) {
  if (!threadId) throw new Error('threadId required');

  const url = new URL(`${apiBase}/agentic/shared-reports/${encodeURIComponent(threadId)}`);
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('offset', String(offset));

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (!res.ok) {
    let err = {};
    try {
      err = await res.json();
    } catch (_) {
      // ignore
    }
    const msg = err?.error || `Failed to fetch public thread (${res.status})`;
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }

  return normalizeAgentThread(await res.json());
}

// Keep old EventSource-based handler for backward compatibility
export function openAgentStreamOld(streamUrl, { onOpen, onToken, onComplete, onError } = {}) {
  console.log('[agentStream] STARTING with streamUrl:', streamUrl);
  const absoluteUrl = streamUrl.startsWith('http') ? streamUrl : `${apiBase}${streamUrl.startsWith('/') ? '' : '/'}${streamUrl}`;
  console.log('[agentStream] absoluteUrl:', absoluteUrl);

  const es = new EventSource(absoluteUrl, { withCredentials: true });
  console.log('[agentStream] EventSource created:', es);
  let lastTextFragment = '';

  es.addEventListener('open', (e) => {
    console.log('[agentStream] open', e);
    onOpen && onOpen(e);
  });

  // Some backends emit incremental tokens as 'token'
  es.addEventListener('token', (e) => {
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] token', data);
      const norm = normalizeToken(data);
      if (norm?.text) lastTextFragment += norm.text;
      onToken && onToken(norm);
    } catch (err) {
      // ignore malformed tokens
    }
  });

  // Handle agent-specific events: plan, planner_meta, final
  es.addEventListener('plan', (e) => {
    // Optional: surface planning as a thought token if desired
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] plan', data);
      const text = typeof data === 'string' ? data : JSON.stringify(data);
      onToken && onToken({ text, type: 'thought' });
    } catch (err) {
      // ignore
    }
  });

  es.addEventListener('planner_meta', () => {
    console.log('[agentStream] planner_meta');
    // Ignore metadata for now
  });

  es.addEventListener('final', (e) => {
    // Emit the final message as a token, then complete
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] final', data);
      const text = typeof data === 'string' ? data : data?.message || data?.text || data?.content || '';
      if (text) {
        lastTextFragment += text;
        onToken && onToken({ text });
      }
    } catch (err) {
      // ignore
    }
    onComplete && onComplete({ lastText: lastTextFragment });
    es.close();
  });

  // Generic message fallback
  es.addEventListener('message', (e) => {
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] message', data);
      const norm = normalizeToken(data);
      if (norm?.text) lastTextFragment += norm.text;
      if (norm?.text) onToken && onToken(norm);
    } catch (err) {
      // ignore
    }
  });

  // Tool events: show as thought or ignore
  es.addEventListener('tool', (e) => {
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] tool', data);
      const summary = typeof data === 'string' ? data : data?.output?.data?.summary || JSON.stringify(data);
      if (summary) onToken && onToken({ text: summary, type: 'thought' });
    } catch (err) {
      // ignore
    }
  });

  es.addEventListener('complete', () => {
    console.log('[agentStream] complete', { lastText: lastTextFragment });
    onComplete && onComplete({ lastText: lastTextFragment });
    es.close();
  });

  es.addEventListener('error', (e) => {
    console.error('[agentStream] error', e);
    onError && onError(e);
    es.close();
  });

  return es;
}

// Lightweight status check to see if orchestrator is still running for a thread
export async function fetchOrchestrateStatus(threadId) {
  if (!threadId) throw new Error('threadId required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = `${apiBase}/agentic/agent/orchestrate/status/${encodeURIComponent(threadId)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    },
    credentials: 'include'
  });
  if (!res.ok) throw new Error(`Failed to fetch status (${res.status})`);
  // Expect { running: true|false }
  return res.json();
}

// Subscribe to future-only SSE events for a running orchestrator
export function subscribeAgentStream(threadId, handlers = {}) {
  if (!threadId) throw new Error('threadId required');
  return connectAgentStream(`${apiBase}/agentic/agent/orchestrate/subscribe/${encodeURIComponent(threadId)}?protocol_version=2`, handlers);
}

// Helpers
function safeParse(str) {
  if (!str) return {};
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
}

function normalizeToken(data) {
  if (data == null) return { text: '' };
  if (typeof data === 'string') return { text: data };
  const text = data.text || data.delta || data.content || data.message || '';
  const type = data.type || data.channel || data.kind;
  return { text, type };
}
